/**
 * Building a food search-result row (`FoodHeader`) from a food (deferred finding df-a01ec78d8abd).
 *
 * Three places turn a food into a row: the search over the user's own foods, the server's search
 * hits, and the picked staple on the food list. They used to build the row by hand, so a field
 * every row should carry had to be added three times, and a missed copy failed silently. This
 * builds the fields they all share; each caller adds its own extras on top (the cached food and
 * the like mark for own foods, the score and other names for server foods).
 *
 * `altNames` is deliberately NOT built here: own foods leave it unset, because their names are on
 * `cachedFood` (see `FoodHeader.altNames`), so each server-side caller copies it itself.
 *
 * Like `food-search-elastic.ts`, this imports only types and pure helpers, so it loads under the
 * node test runner; the formatter arrives as a parameter for that reason.
 */

import type { FoodHeader, FoodStatus, Nutrients } from '@cf-shared/data-model/food';
import type { UiDisplayNutrient } from 'models/display-nutrient';
import { isLegacyMealId, isRecipeById } from '@cf-shared/data-model/recipe';

/** the part of a food a row is built from; a full `Food` and a server hit's source both have it */
export interface FoodHeaderSource {
  _id: string;
  name: string;
  /** optional because a stored `Food` may lack it; passed to the formatter as it is, as every
   *  hand-built copy did */
  nutrients?: Nutrients;
}

export interface FoodHeaderParts {
  /** turns the food's stored nutrients into the numbers the list shows */
  formatNutrients: (nutrients: Nutrients) => UiDisplayNutrient[];
  /** whether the food is official, private, contributed and so on */
  status: FoodStatus | undefined;
  /** whether the food is a recipe; when not given it is worked out from the id alone. The own-food
   *  search passes `isRecipe(food)`, which also checks the food really has ingredients */
  recipe?: boolean;
}

/** The fields every food search-result row carries, built from one food. */
export function buildFoodHeader(food: FoodHeaderSource, parts: FoodHeaderParts): FoodHeader {
  return {
    _id: food._id,
    name: food.name,
    status: parts.status,
    displayNutrients: parts.formatNutrients(food.nutrients),
    recipe: parts.recipe ?? isRecipeById(food._id),
    meal: isLegacyMealId(food._id),
  };
}
